/**
 * Public, mobile-first demo-voting page. Attendees scan a QR from the
 * presenter's screen, land here unauthenticated, and rate the demo 1–5 with
 * optional written pros/cons. The backend keys each attendee by an httponly
 * `boi-vote-id` cookie, so re-visiting pre-fills an editable version of the
 * vote they already cast.
 */

import { Check, Loader2, Pencil } from "lucide-react";
import { useState } from "react";

import { JJWordmark } from "@/components/jj-wordmark";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { useMyVote, useSubmitVote, type VoteOut } from "@/lib/api";
import { logger } from "@/lib/logger";
import { cn } from "@/lib/utils";

const SCORES = [1, 2, 3, 4, 5];
// Mirror the backend caps so the client never submits more than the API keeps.
const MAX_ITEMS = 10;
const MAX_ITEM_LEN = 300;

/**
 * Every non-empty point the attendee has typed, pre-cap. The over-cap warning
 * counts these, so what we warn about can never drift from what we send.
 */
function parsePoints(raw: string): string[] {
  return raw
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean);
}

/** What actually goes to the API: the first MAX_ITEMS points, each length-capped. */
function parseLines(raw: string): string[] {
  return parsePoints(raw)
    .slice(0, MAX_ITEMS)
    .map((line) => line.slice(0, MAX_ITEM_LEN));
}

export function VotePage() {
  const { data, isLoading } = useMyVote();

  return (
    <div className="bg-background flex min-h-screen items-center justify-center px-4 py-10">
      <div className="w-full max-w-md">
        {isLoading ? (
          <div className="flex justify-center py-24">
            <Loader2 className="text-muted h-6 w-6 animate-spin" aria-label="Loading" />
          </div>
        ) : (
          <VoteForm existing={data?.vote ?? null} alreadyVoted={data?.voted ?? false} />
        )}
      </div>
    </div>
  );
}

function VoteForm({ existing, alreadyVoted }: { existing: VoteOut | null; alreadyVoted: boolean }) {
  const submitVote = useSubmitVote();
  const [score, setScore] = useState<number | null>(existing?.score ?? null);
  const [pros, setPros] = useState((existing?.pros ?? []).join("\n"));
  const [cons, setCons] = useState((existing?.cons ?? []).join("\n"));
  const [error, setError] = useState("");
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    if (score == null) {
      setError("Please pick a score from 1 to 5.");
      return;
    }
    logger.debug("[vote] submitting score", score);
    try {
      await submitVote.mutateAsync({ score, pros: parseLines(pros), cons: parseLines(cons) });
      logger.debug("[vote] submit succeeded");
      setSubmitted(true);
    } catch (err) {
      logger.warn("[vote] submit failed:", err);
      setError("Could not submit your vote. Please try again.");
    }
  };

  if (submitted) {
    return (
      <Card className="border-border shadow-lg">
        <CardContent className="flex flex-col items-center gap-4 px-6 py-12 text-center">
          {/* Green is reserved for data-viz/status accents — this success tick
              is exactly that, used once and softly. */}
          <span className="bg-jj-green-01/20 flex h-14 w-14 items-center justify-center rounded-full">
            <Check className="text-jj-green-05 h-7 w-7" aria-hidden />
          </span>
          <div className="space-y-1">
            <h2 className="font-heading text-foreground text-xl font-bold">
              Thanks for your feedback
            </h2>
            <p className="text-muted text-sm">Your response has been recorded.</p>
          </div>
          <Button
            type="button"
            variant="ghost"
            onClick={() => setSubmitted(false)}
            className="text-muted gap-1.5"
          >
            <Pencil className="h-3.5 w-3.5" aria-hidden />
            Edit your response
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-border shadow-lg">
      <CardHeader className="text-center">
        <JJWordmark size="sm" className="mx-auto mb-4" />
        {/* The score picker below is this view's single hero, paired with the
            red submit CTA — the same 2-red budget as login.tsx. */}
        <CardTitle className="font-heading text-foreground text-2xl font-bold">
          How would you rate this demo?
        </CardTitle>
        {alreadyVoted ? (
          <p className="text-muted text-sm">
            You already voted — you can update your response below.
          </p>
        ) : (
          <p className="text-muted text-sm">Tap a score, then add a few thoughts if you like.</p>
        )}
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-6">
          <div
            role="group"
            aria-label="Rate this demo from 1 to 5"
            className="flex justify-between gap-2"
          >
            {SCORES.map((value) => {
              const selected = score === value;
              return (
                <button
                  key={value}
                  type="button"
                  aria-pressed={selected}
                  aria-label={`${value} out of 5`}
                  onClick={() => setScore(value)}
                  className={cn(
                    "focus-visible:ring-ring flex h-14 flex-1 items-center justify-center rounded-md border text-lg font-medium transition-colors focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none",
                    selected
                      ? "border-accent bg-accent text-accent-foreground"
                      : "border-input bg-card text-foreground hover:border-accent/40",
                  )}
                >
                  {value}
                </button>
              );
            })}
          </div>

          <PointsField id="pros" label="What worked well?" value={pros} onChange={setPros} />

          <PointsField id="cons" label="What could be better?" value={cons} onChange={setCons} />

          {error && <p className="text-destructive text-sm">{error}</p>}

          <Button
            type="submit"
            disabled={submitVote.isPending}
            className="bg-accent text-accent-foreground hover:bg-accent/90 h-12 w-full text-base"
          >
            {submitVote.isPending ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Submitting…
              </>
            ) : alreadyVoted ? (
              "Update my vote"
            ) : (
              "Submit vote"
            )}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}

/**
 * A "one point per line" textarea. Once the attendee types past the cap we say
 * so in plain language instead of silently dropping the overflow at submit —
 * the text itself is never truncated as they type, and submit stays enabled.
 */
function PointsField({
  id,
  label,
  value,
  onChange,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (next: string) => void;
}) {
  const count = parsePoints(value).length;
  const overCap = count > MAX_ITEMS;

  return (
    <div className="space-y-2">
      <label htmlFor={id} className="text-foreground text-sm font-medium">
        {label}
      </label>
      <Textarea
        id={id}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="One point per line"
        rows={3}
      />
      <p role="status" className={cn("text-xs", overCap ? "text-muted" : "text-warm-gray-06")}>
        {overCap
          ? `Only the first ${MAX_ITEMS} points will be sent — you have ${count}.`
          : "One point per line."}
      </p>
    </div>
  );
}
